import { supabase } from '../lib/supabase';
import type { Empresa } from '../models/empresa.model';

export const empresaService = {
  async buscarPorId(empresaId: string): Promise<Empresa | null> {
    const { data, error } = await supabase
      .from('empresas')
      .select('*')
      .eq('id', empresaId)
      .maybeSingle();

    if (error) throw error;
    return (data as Empresa | null) ?? null;
  },

  async buscarStatus(empresaId: string): Promise<Empresa['status'] | null> {
    const { data, error } = await supabase
      .from('empresas')
      .select('status')
      .eq('id', empresaId)
      .maybeSingle();

    if (error) throw error;
    return (data as { status: Empresa['status'] } | null)?.status ?? null;
  },

  /**
   * Verifica o acesso ao Jukebox a partir do telaId (rota pública / anon).
   * Usa a RPC SECURITY DEFINER verificar_acesso_jukebox para resolver
   * tela → empresa → planos_config sem expor as tabelas ao anon.
   */
  async verificarAcessoJukebox(telaId: string): Promise<{
    status:       Empresa['status'] | null;
    jukeboxAtivo: boolean;
  }> {
    const { data, error } = await supabase
      .rpc('verificar_acesso_jukebox' as never, { p_tela_id: telaId } as never)
      .maybeSingle();

    if (error) throw error;

    const row = data as { status: Empresa['status']; jukebox_ativo: boolean } | null;
    return { status: row?.status ?? null, jukeboxAtivo: row?.jukebox_ativo ?? false };
  },

  async buscarStatusPorTela(telaId: string): Promise<Empresa['status'] | null> {
    // O Player é anon e não tem SELECT em telas/empresas: a leitura direta devolvia sempre null.
    // A RPC (SECURITY DEFINER) devolve só o status da empresa dona da tela.
    const { data, error } = await supabase.rpc(
      'player_status_empresa' as never,
      { p_tela_id: telaId } as never,
    );

    // Checagem comercial, não de segurança: se a RPC falhar a TV segue tocando (o broadcast
    // `conta_suspensa` e a próxima carga ainda bloqueiam).
    if (error) {
      console.warn('[Player] status da empresa indisponível:', (error as { message?: string }).message);
      return null;
    }

    return (data as Empresa['status'] | null) ?? null;
  },

  async verificarLimiteTelas(empresaId: string): Promise<{
    permitido: boolean;
    total:     number;
    limite:    number;
  }> {
    const [{ data: empresa, error: erroEmpresa }, { count, error: erroContagem }] = await Promise.all([
      supabase.from('empresas').select('limite_telas').eq('id', empresaId).single(),
      supabase.from('telas').select('id', { count: 'exact', head: true }).eq('empresa_id', empresaId),
    ]);

    if (erroEmpresa) throw erroEmpresa;
    if (erroContagem) throw erroContagem;

    const limite = (empresa as { limite_telas: number }).limite_telas;
    const total  = count ?? 0;

    return { permitido: total < limite, total, limite };
  },
};
